import { isEmpty } from 'lodash';
import * as mux from '../../../components/video-library/mux';
import * as enums from '@client/enums';
import type { MuxVideo } from '@client/new/common/videos/videos.helpers';

export async function influencerMuxVideoUpload(
  influencerId: number,
  file: File,
  config: {
    thumbnailPosition?: number;
    onProgress?: (progress: number) => void;
  } = {},
): Promise<MuxVideo> {
  const { thumbnailPosition, onProgress } = config ?? {};

  const data = await mux.generateMuxSignedUrl({
    influencerId,
    fileSize: file.size,
    title: file.name,
    thumbnailPosition: thumbnailPosition ?? 0,
    bucketName: enums.MUX_BUCKETS.LANDING_PAGE_BUILDER,
  });
  const videoId = data.video.id;
  const muxUploadInstance = mux.upload({
    file: file,
    url: data && data.signedURL,
    chunkSize: 5120,
    onProgress: (progress: number) => {
      onProgress && onProgress(progress);
    },
  });

  return new Promise((resolve, reject) => {
    muxUploadInstance.on('success', async () => {
      let desiredQuality = 'high';
      const videoResponse = await polling(
        1000,
        (r) => {
          const streamCreated = r.video?.video !== null;
          if (r.video?.width < 480) {
            desiredQuality = 'low';
          } else if (r.video?.width < 720) {
            desiredQuality = 'medium';
          }
          const mp4created = !isEmpty(r.video?.downloadLink?.[desiredQuality]);
          return streamCreated && mp4created;
        },
        async () => await mux.fetchVideo(videoId),
      );

      resolve(videoResponse.video);
    });
    muxUploadInstance.on('error', (e: unknown) => {
      console.error('Error uploading influencer video to mux', e);
      reject(e);
    });
  });
}

async function polling<T>(
  timeout: number,
  isSuccessDef: (x: T) => boolean,
  asyncFn: () => Promise<T>,
) {
  let maxTries = 1000;
  while (maxTries > 0) {
    const result = await asyncFn();

    const isSuccess = isSuccessDef(result);

    if (isSuccess) {
      return result;
    }

    await sleep(timeout);
    maxTries -= 1;
  }
}

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
